// СЪСТОЯНИЕТО на разнасянето на отчет (hook, без рендер): алокации по
// продукт/склад с „балансьор" (складът, който поема остатъка), недостизи,
// избраните заместители и сумите Нето/Бруто. Държи се отделно от рендера,
// защото картите (Замяна / Продадено / Изписване) и футърът четат от него.
import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useInterchangeGroups } from "../../../../hooks/useInventory";
import {
  getStockBalances,
  type CreateReportPostingLine,
  type ReportPostingPreview,
  type ReportSubstitutionItem,
  type StockBalance,
} from "../../../../api/inventory";
import {
  buildSubstitutionIssues,
  donorQuantityFor,
} from "./reportPostingSubstitutionLogic";
import { initialAllocationsOf } from "./reportPostingDefaults";
import {
  newPostingAllocation,
  QTY_EPSILON,
  type PostingAllocation,
  type PostingAllocationsByProduct,
} from "./reportPostingShared";

type Params = {
  preview: ReportPostingPreview | undefined;
  grossFactorOf: (productId: number) => number;
  onTouch: () => void;
};

export const useReportPostingWorksheetState = ({
  preview,
  grossFactorOf,
  onTouch,
}: Params) => {
  const [allocations, setAllocations] = useState<PostingAllocationsByProduct>(
    {},
  );
  // Складът-балансьор по продукт (поема остатъка при разпределяне по складове).
  const [balancers, setBalancers] = useState<Record<number, number | null>>({});
  const [substitutions, setSubstitutions] = useState<ReportSubstitutionItem[]>(
    [],
  );
  // Нето себестойност по алокация (пише я useWarehouseCost от редовете).
  const [costs, setCosts] = useState<Record<number, number | null>>({});

  // Инициализация от preview-то (при редакция — и запазеното). Вика се от
  // работния лист точно веднъж, когато данните са свежи.
  // `prefillSubstitutions` — досегашните замени при „Коригирай и разнеси
  // повторно" (анулираният документ ги е носил).
  const initializeFromPreview = (
    data: ReportPostingPreview,
    prefillSubstitutions?: ReportSubstitutionItem[],
  ) => {
    const next: PostingAllocationsByProduct = {};
    const nextBalancers: Record<number, number | null> = {};
    const savedByProduct = new Map<number, PostingAllocation[]>();
    for (const saved of data.savedLines) {
      const allocation = newPostingAllocation(saved.warehouseId, saved.quantity);
      allocation.mode = saved.mode;
      allocation.lotId = saved.lotId;
      const list = savedByProduct.get(saved.productId) ?? [];
      list.push(allocation);
      savedByProduct.set(saved.productId, list);
    }
    for (const product of data.products) {
      const saved = savedByProduct.get(product.productId);
      if (saved && saved.length) {
        next[product.productId] = saved;
        // балансьорът = последният склад (носи остатъка; запазените суми остават коректни)
        nextBalancers[product.productId] = saved[saved.length - 1].warehouseId;
      } else {
        // Предизборът идва от BE по съставка (свой склад на продукта > склад
        // на станцията): няколко позиции с явни количества, последната поема
        // остатъка. БЕЗ fallback по наличност — без предизбор колоната стои
        // празна, докато потребителят не избере.
        const initial = initialAllocationsOf(product);
        next[product.productId] = initial.map((allocation) =>
          newPostingAllocation(allocation.warehouseId, allocation.quantity),
        );
        nextBalancers[product.productId] = initial.length
          ? initial[initial.length - 1].warehouseId
          : null;
      }
    }
    setAllocations(next);
    setBalancers(nextBalancers);
    setSubstitutions(
      prefillSubstitutions ??
        data.savedSubstitutions.map((saved) => ({
          warehouseId: saved.warehouseId,
          recipientProductId: saved.recipientProductId,
          donorProductId: saved.donorProductId,
          quantity: saved.quantity,
        })),
    );
    setCosts({});
  };

  // Наличност по (продукт, склад) от preview-то. КЛАМПНАТА до 0, както навсякъде
  // в модула (Наличности, инвентаризация): на рафта не може да има под 0 —
  // отрицателният баланс е стара дупка и живее в „Излишък от продажби".
  // Оттук се хранят и колоната „Наличност", и недостизите, и компенсациите, за
  // да не излиза липса „нужно + старата дупка".
  const availableOf = useMemo(() => {
    const map = new Map<string, number>();
    for (const product of preview?.products ?? [])
      for (const wh of product.warehouses)
        map.set(
          `${product.productId}:${wh.warehouseId}`,
          Math.max(0, wh.available),
        );
    return (productId: number, warehouseId: number) =>
      map.get(`${productId}:${warehouseId}`) ?? 0;
  }, [preview]);

  // Опции за падащото „Склад" по продукт (само името; наличността е колона).
  const warehouseOptionsOf = useMemo(() => {
    const map = new Map<
      number,
      { value: number; label: string; available: number }[]
    >();
    for (const product of preview?.products ?? []) {
      map.set(
        product.productId,
        product.warehouses.map((wh) => ({
          value: wh.warehouseId,
          label: wh.warehouseName ?? `#${wh.warehouseId}`,
          available: Math.max(0, wh.available),
        })),
      );
    }
    return map;
  }, [preview]);

  // Покрито от замяна по получател (намалява количеството за изписване).
  const coveredOf = useMemo(() => {
    const map = new Map<number, number>();
    for (const sub of substitutions)
      map.set(
        sub.recipientProductId,
        (map.get(sub.recipientProductId) ?? 0) + sub.quantity,
      );
    return map;
  }, [substitutions]);

  // Нужно количество по продукт (от разгънатите рецепти).
  const requiredOf = useMemo(() => {
    const map = new Map<number, number>();
    for (const product of preview?.products ?? [])
      map.set(product.productId, product.requiredQuantity);
    return map;
  }, [preview]);

  // Реалното количество за разпределяне = нужно − покрито от замяна.
  const toAllocateOf = (productId: number) =>
    Math.max(0, (requiredOf.get(productId) ?? 0) - (coveredOf.get(productId) ?? 0));

  // Складът-балансьор за продукт; fallback = последният избран.
  const balancerOf = (productId: number) => {
    const ids = (allocations[productId] ?? []).map((a) => a.warehouseId);
    const balancer = balancers[productId];
    return balancer != null && ids.includes(balancer)
      ? balancer
      : (ids[ids.length - 1] ?? null);
  };

  // Ефективно количество за (продукт, склад): балансьорът поема остатъка,
  // останалите — въведената стойност.
  const effectiveQtyOf = (productId: number, warehouseId: number) => {
    const list = allocations[productId] ?? [];
    const balancer = balancerOf(productId);
    if (warehouseId === balancer) {
      const explicit = list
        .filter((a) => a.warehouseId !== balancer)
        .reduce((sum, a) => sum + (a.quantity || 0), 0);
      return toAllocateOf(productId) - explicit;
    }
    return list.find((a) => a.warehouseId === warehouseId)?.quantity ?? 0;
  };

  // „След" = наличност − за изписване. Прогноза: МОЖЕ да е под нула (тогава
  // продуктът ще се изпише с отрицателна наличност) — само за колоната „След";
  // наличността сама остава клампната до 0.
  const afterOf = (productId: number, warehouseId: number) =>
    availableOf(productId, warehouseId) - effectiveQtyOf(productId, warehouseId);

  // Недостиг по продукт = Σ max(0, ефективно − наличност) по складовете му.
  const shortageByProduct = useMemo(() => {
    const map = new Map<number, number>();
    for (const product of preview?.products ?? []) {
      let shortage = 0;
      for (const allocation of allocations[product.productId] ?? []) {
        const need = effectiveQtyOf(product.productId, allocation.warehouseId);
        const available = availableOf(product.productId, allocation.warehouseId);
        if (need > available) shortage += need - available;
      }
      map.set(product.productId, shortage > QTY_EPSILON ? shortage : 0);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, allocations, balancers, availableOf, coveredOf, requiredOf]);

  const shortageCount = useMemo(
    () => [...shortageByProduct.values()].filter((v) => v > 0).length,
    [shortageByProduct],
  );
  const totalNet = useMemo(
    () => Object.values(costs).reduce((sum: number, v) => sum + (v ?? 0), 0),
    [costs],
  );
  // Общо бруто: нетото на всеки продукт × неговия фактор.
  const totalGross = useMemo(() => {
    let sum = 0;
    for (const [productIdStr, list] of Object.entries(allocations)) {
      const net = list.reduce((s, a) => s + (costs[a.id] ?? 0), 0);
      sum += net * grossFactorOf(Number(productIdStr));
    }
    return sum;
  }, [allocations, costs, grossFactorOf]);

  // Складове, замесени в текущите алокации (за групи/наличности при компенсиране).
  const involvedWarehouseIds = useMemo(() => {
    const set = new Set<number>();
    for (const list of Object.values(allocations))
      for (const allocation of list) set.add(allocation.warehouseId);
    return [...set];
  }, [allocations]);

  // Групите взаимозаменяеми са ОБЩИ за всички складове → ЕДНА заявка (до 0.0.150
  // се пускаше по една на склад с еднакъв резултат — остатък от per-склад групите).
  const { data: interchangeGroups = [] } = useInterchangeGroups();
  const balanceQueries = useQueries({
    queries: involvedWarehouseIds.map((warehouseId) => ({
      queryKey: ["inventory", "stock", warehouseId],
      queryFn: () => getStockBalances(warehouseId),
    })),
  });
  const balancesByWarehouse = useMemo(() => {
    const map = new Map<number, Map<number, StockBalance>>();
    involvedWarehouseIds.forEach((warehouseId, i) => {
      const data = balanceQueries[i]?.data;
      if (data) map.set(warehouseId, new Map(data.map((b) => [b.productId, b])));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [involvedWarehouseIds, balanceQueries.map((q) => q.data)]);

  // Имена/единици по продукт (за компенсациите): от отчета + наличностите.
  const nameById = useMemo(() => {
    const map = new Map<number, { name: string; unit: string | null }>();
    for (const product of preview?.products ?? [])
      map.set(product.productId, {
        name: product.productName,
        unit: product.unit,
      });
    for (const balances of balancesByWarehouse.values())
      for (const balance of balances.values())
        if (!map.has(balance.productId))
          map.set(balance.productId, {
            name: balance.productName,
            unit: balance.unit,
          });
    return map;
  }, [preview, balancesByWarehouse]);

  // ─── Действия по алокациите ───────────────────────────────────────────────
  // Смяна на избраните складове (multi-select): пазим същите алокации за
  // оставащите, добавяме нови за новоизбраните, махаме премахнатите.
  const setWarehouses = (productId: number, warehouseIds: number[]) => {
    if (warehouseIds.length === 0) return; // поне един склад
    setAllocations((state) => {
      const current = state[productId] ?? [];
      const byWarehouse = new Map(current.map((a) => [a.warehouseId, a]));
      const next = warehouseIds.map(
        (id) => byWarehouse.get(id) ?? newPostingAllocation(id, null),
      );
      return { ...state, [productId]: next };
    });
    setBalancers((state) => {
      const current = state[productId];
      return {
        ...state,
        [productId]:
          current != null && warehouseIds.includes(current)
            ? current
            : warehouseIds[warehouseIds.length - 1],
      };
    });
    onTouch();
  };

  // Въвеждане на количество за склад (балансьорът поема остатъка; въведе ли
  // се в балансьора, ролята минава към друг склад).
  const setAllocationQuantity = (
    productId: number,
    warehouseId: number,
    value: number | null,
  ) => {
    const ids = (allocations[productId] ?? []).map((a) => a.warehouseId);
    const balancer = balancerOf(productId);
    if (warehouseId === balancer && ids.length > 1) {
      const newBalancer =
        [...ids].reverse().find((id) => id !== warehouseId) ?? warehouseId;
      setBalancers((state) => ({ ...state, [productId]: newBalancer }));
    }
    setAllocations((state) => ({
      ...state,
      [productId]: (state[productId] ?? []).map((a) =>
        a.warehouseId === warehouseId ? { ...a, quantity: value } : a,
      ),
    }));
    onTouch();
  };

  const setAllocationCost = (allocationId: number, value: number | null) =>
    setCosts((state) =>
      state[allocationId] === value ? state : { ...state, [allocationId]: value },
    );

  // ─── Замяна чрез взаимозаменяеми ─────────────────────────────────────────
  // Редовете с недостиг и възможните им заместители (чистата логика е в
  // reportPostingSubstitutionLogic.ts — строи се от недостига ПРЕДИ замените).
  const substitutionIssues = buildSubstitutionIssues({
    preview,
    warehouseIdsOf: (productId) =>
      (allocations[productId] ?? []).map((allocation) => allocation.warehouseId),
    balancerOf,
    effectiveQtyOf,
    availableOf,
    coveredOf,
    substitutions,
    interchangeGroups,
    balancesByWarehouse,
  });

  // Избор на количество от заместител (в единиците на получателя); null или
  // 0 = заместителят се маха.
  const setSubstitution = (
    warehouseId: number,
    recipientProductId: number,
    donorProductId: number,
    quantity: number | null,
  ) => {
    setSubstitutions((current) => {
      const rest = current.filter(
        (x) =>
          !(
            x.warehouseId === warehouseId &&
            x.recipientProductId === recipientProductId &&
            x.donorProductId === donorProductId
          ),
      );
      if (quantity == null || quantity <= QTY_EPSILON) return rest;
      return [...rest, { warehouseId, recipientProductId, donorProductId, quantity }];
    });
    onTouch();
  };

  // Колко е взето от донор в склад (в неговите единици) от ВСИЧКИ избрани
  // замени — за подредовете „⇄ вместо X" в изписването.
  const donorTakenOf = (warehouseId: number, donorProductId: number) =>
    substitutions
      .filter((sub) => sub.warehouseId === warehouseId && sub.donorProductId === donorProductId)
      .reduce(
        (sum, sub) =>
          sum +
          (donorQuantityFor(
            interchangeGroups,
            sub.recipientProductId,
            sub.donorProductId,
            sub.quantity,
          ) ?? 0),
        0,
      );
  const donorStockOf = (warehouseId: number, donorProductId: number) =>
    Math.max(0, balancesByWarehouse.get(warehouseId)?.get(donorProductId)?.quantity ?? 0);

  // Количество на донора за замяна (в неговите единици) — през коефициентите
  // на групата. null, когато групата (още) не е заредена.
  const donorQuantityOf = (sub: ReportSubstitutionItem): number | null =>
    donorQuantityFor(
      interchangeGroups,
      sub.recipientProductId,
      sub.donorProductId,
      sub.quantity,
    );

  // ─── Запис ────────────────────────────────────────────────────────────────
  const buildLines = (): CreateReportPostingLine[] => {
    const out: CreateReportPostingLine[] = [];
    for (const [productIdStr, list] of Object.entries(allocations)) {
      const productId = Number(productIdStr);
      for (const allocation of list) {
        const quantity = effectiveQtyOf(productId, allocation.warehouseId);
        if (quantity <= 0) continue;
        out.push({
          productId,
          warehouseId: allocation.warehouseId,
          quantity,
          mode: allocation.mode,
          lotId: allocation.mode === "lot" ? allocation.lotId : null,
        });
      }
    }
    return out;
  };

  // Продукти за изписване без нито един избран склад — блокират разнасянето с
  // ясно съобщение (иначе BE връща подвеждащото „Рецептите са променени").
  const productsWithoutWarehouse = () =>
    (preview?.products ?? []).filter(
      (product) =>
        toAllocateOf(product.productId) > QTY_EPSILON &&
        (allocations[product.productId] ?? []).length === 0,
    );

  return {
    allocations,
    costs,
    substitutions,
    initializeFromPreview,
    availableOf,
    warehouseOptionsOf,
    coveredOf,
    toAllocateOf,
    balancerOf,
    effectiveQtyOf,
    afterOf,
    shortageByProduct,
    shortageCount,
    totalNet,
    totalGross,
    nameById,
    setWarehouses,
    setAllocationQuantity,
    setAllocationCost,
    substitutionIssues,
    setSubstitution,
    donorTakenOf,
    donorStockOf,
    donorQuantityOf,
    buildLines,
    productsWithoutWarehouse,
  };
};

export type ReportPostingWorksheetState = ReturnType<
  typeof useReportPostingWorksheetState
>;
